import {
    type FormikBag,
    type FormikValues,
    type InjectedFormikProps,
    withFormik,
    type WithFormikConfig,
} from "formik";
import { useCallback, useEffect, useRef, useState } from "react";

import {
    getDirtyValues,
    type IDirtyTrackingConfig,
    type IDirtyTrackingInjectedProps,
} from "commonComponents/utilities/DirtyTracking/DirtyTracking.utilities";
import {
    type IDirtyTrackingNavigationGuardResult,
    useDirtyTrackingNavigationGuard,
} from "commonComponents/utilities/DirtyTracking/useDirtyTrackingNavigationGuard";

// Formik's out-of-the-box config plus those needed by our wrappers. We widen
// `handleSubmit` to allow returning a Promise: Formik 1.5.8 ignores onSubmit's return
// value at runtime (executeSubmit discards it), so this is purely for our wrappers —
// withFormikBT awaits it so the navigation guard's Save waits for the real (async) save.
// Resolve `false` to tell the guard the save didn't happen (e.g. validation failed) so it
// stays on the page; any other result lets navigation proceed.
type IBTWithFormikConfig<Props, Values extends FormikValues, Payload = Values> = Omit<
    WithFormikConfig<Props, Values, Payload>,
    "handleSubmit"
> &
    IDirtyTrackingConfig<Values> & {
        handleSubmit: (
            values: Payload,
            formikBag: FormikBag<Props, Values>,
        ) => void | Promise<void | boolean>;
        /**
         * Opts the form into the navigation guard that prompts on unsaved changes when leaving
         * via react-router navigation or browser unload (refresh / tab close). Set it on forms
         * that already render the unsaved-changes prompt (via `showUnsavedChangesPrompt` or
         * `getUnsavedChangesModalConfig`) to extend that same prompt to those events.
         *
         * `true` for an unconditional opt-in, or a **predicate of the wrapped component's props**
         * to arm it only in certain modes — e.g. an autosaving form arms the guard only when
         * autosave is off (`(props) => !props.autosaveEnabled`), since autosave keeps the form
         * permanently dirty and a guard would then prompt on every exit. Evaluated on each render,
         * so it reacts to prop changes; keep the predicate a pure read of props.
         *
         * Requires dirty tracking: the guard reads `isDirty` to decide whether to prompt, so
         * `withFormikBT` ANDs this with `enableDirtyTracking !== false` (a `false` there wins).
         * @default false
         */
        enableNavigationGuard?: boolean | ((props: Props) => boolean);
    };

// Formik's out-of-the-box injected props combined with those injected from our wrappers
export type IBTInjectedFormikProps<Props, Values extends FormikValues> = InjectedFormikProps<
    Props,
    Values
> &
    IDirtyTrackingInjectedProps<Values> &
    IDirtyTrackingNavigationGuardResult;

/**
 * This takes our extension of Formik's config and returns a wrapper for a component that
 * expects formik's injected props as well as those from our custom wrappers
 */
export const withFormikBT = <Props, Values extends FormikValues>(
    wrappedConfig: IBTWithFormikConfig<Props, Values>,
) => {
    // Formik 1.5.8's `submitForm()` resolves after validation only and discards the
    // value `handleSubmit` (onSubmit) returns, so the navigation guard's Save can't
    // tell when an async save finishes. We wrap the config's `handleSubmit` to capture
    // the promise it returns, then await it in `handleSubmitWrapper`. Scoped per
    // `withFormikBT` call (i.e. per form), which is effectively a singleton on screen.
    // A form whose `handleSubmit` returns nothing keeps the old behavior (resolves
    // immediately); to make the guard's Save wait, the config must RETURN its save promise.
    let pendingSave: Promise<void | boolean> | undefined;
    // Read through a function so callers get the declared type: TS narrows `pendingSave` to
    // `undefined` after the reset in handleSubmitWrapper and can't see that submitForm()
    // mutates it as a side effect via this closure.
    const readPendingSave = (): Promise<void | boolean> | undefined => pendingSave;
    const originalHandleSubmit = wrappedConfig.handleSubmit;
    const capturingConfig: IBTWithFormikConfig<Props, Values> = {
        ...wrappedConfig,
        handleSubmit: (submittedValues, formikBag) => {
            const result = originalHandleSubmit(submittedValues, formikBag);
            pendingSave = Promise.resolve(result);
            return result;
        },
    };

    const wrapperHOC = (Component: React.ComponentType<IBTInjectedFormikProps<Props, Values>>) => {
        // Expects injected props from withFormik - handle all custom wrapper logic in here
        const WrapperComponent: React.FC<InjectedFormikProps<Props, Values>> = (props) => {
            const { setFieldValue, resetForm, values, submitForm, initialValues } = props;
            const [overriddenValues, setOverriddenValues] = useState<Partial<Values>>({});

            // On reinitialize (enableReinitialize) a fresh baseline arrives, so stale overrides must
            // be dropped - some save paths reinitialize without going through our wrapped
            // resetForm/handleSubmit, the only other places overriddenValues is cleared. Skip the
            // initial mount: effects flush bottom-up, so clearing here would wipe an override a child
            // just stashed on mount. Only clear on an initialValues change after the first render.
            const isFirstRender = useRef(true);
            useEffect(() => {
                if (isFirstRender.current) {
                    isFirstRender.current = false;
                    return;
                }
                setOverriddenValues({});
            }, [initialValues]);

            const handleSetFieldValueWithoutDirtyTracking = useCallback(
                (field: string, value: unknown) => {
                    setFieldValue(field, value);
                    // Use a functional update so multiple calls in the same tick
                    // don't overwrite previous values that were also updated.
                    setOverriddenValues((prev) => ({ ...prev, [field]: value }));
                },
                [setFieldValue],
            );

            const handleResetForm = (nextValues?: Values) => {
                resetForm(nextValues);
                setOverriddenValues({});
            };

            // Formik 1.x types submitForm as void, but at runtime it returns a Promise
            // that resolves after validation. `handleSubmit` discards it, so we call
            // submitForm directly, then await the save the config's handleSubmit returned
            // (captured above) so the navigation guard's Save doesn't navigate before the
            // save completes.
            //
            // Returns the guard's save signal (boolean): `false` when validation blocked the
            // save (config handleSubmit never ran) or the save reported it didn't persist;
            // `true` otherwise. A config handleSubmit that resolves `void` counts as saved.
            const handleSubmitWrapper = async (
                e?: React.FormEvent<HTMLFormElement>,
            ): Promise<boolean> => {
                e?.preventDefault?.();
                e?.stopPropagation?.();
                pendingSave = undefined;
                await (submitForm() as unknown as Promise<unknown>);
                // submitForm() set this as a side effect when the form is valid; if it's still
                // undefined, validation blocked the submit — tell the guard not to navigate.
                const savePromise = readPendingSave();
                if (!savePromise) {
                    return false;
                }
                const saved = await savePromise;
                setOverriddenValues({});
                return saved !== false;
            };

            const getValues = () => {
                const newInitialValues = {
                    ...initialValues,
                    ...overriddenValues,
                };
                return {
                    initialValues: newInitialValues,
                    currentValues: values,
                    setFieldValueWithoutDirtyTracking: handleSetFieldValueWithoutDirtyTracking,
                };
            };

            const baseIgnoreValues = ["attachedFilesConfiguration"];
            const ignoreDirtyValues = [
                ...(wrappedConfig.ignoreDirtyValues ?? []),
                ...baseIgnoreValues,
            ];

            // getDirtyValues only reads the dirty-tracking fields (enableDirtyTracking,
            // ignoreDirtyValues) — enableNavigationGuard is a withFormikBT-level concern, not
            // an IDirtyTrackingConfig field, so it isn't passed here.
            const dirtyProps = getDirtyValues(getValues, {
                ignoreDirtyValues,
                enableDirtyTracking: wrappedConfig.enableDirtyTracking,
            });
            const { navigateWithoutPrompt, registerGuardSave } = useDirtyTrackingNavigationGuard({
                getDirtyState: dirtyProps.getDirtyTrackingProps,
                onSave: handleSubmitWrapper,
                onDiscard: () => handleResetForm(initialValues),
                // The guard needs isDirty to decide whether to prompt; enableDirtyTracking: false
                // forces isDirty false, so a guard here could never prompt — and as a stack entry it
                // would park a dirty parent beneath it. Arm the guard only when dirty tracking is on.
                // The config flag may be a predicate of the wrapped props (e.g. arm only in the mode
                // that renders the prompt), so resolve it against the current props each render.
                enableNavigationGuard:
                    wrappedConfig.enableDirtyTracking !== false &&
                    (typeof wrappedConfig.enableNavigationGuard === "function"
                        ? wrappedConfig.enableNavigationGuard(props)
                        : (wrappedConfig.enableNavigationGuard ?? false)),
            });

            return (
                <Component
                    {...props}
                    resetForm={handleResetForm}
                    handleSubmit={handleSubmitWrapper}
                    navigateWithoutPrompt={navigateWithoutPrompt}
                    registerGuardSave={registerGuardSave}
                    {...dirtyProps}
                />
            );
        };

        return withFormik<Props, Values>(capturingConfig)(WrapperComponent);
    };

    return wrapperHOC;
};
